import { useEffect } from 'react';
import { FiArrowLeft, FiX } from 'react-icons/fi';
import GoogleMapEmbed from '../../../../component/shared/GoogleMapEmbed';
import { useDirectionsOrigin } from '../../../../component/shared/useDirectionsOrigin';
import { StationHours } from './VeterinaryStations';
import { STATE_CAPITAL_COORDS } from '../../data/nigeriaStates';
import { scrollToTop } from '../motion/ScrollToTop';

// Exact coordinates where the record has them, else its state capital
function coordsOf(record) {
  if (record.lat != null && record.lng != null) return `${record.lat},${record.lng}`;
  const capital = STATE_CAPITAL_COORDS[record.state];
  return capital ? capital.join(',') : `${record.state}, Nigeria`;
}

function Detail({ label, value }) {
  if (value == null || value === '') return null;
  return (
    <p className="text-[12px] text-[#8e8e8e]">
      {label}: <span className="text-[#333]">{value}</span>
    </p>
  );
}

/**
 * Map view for one record, opened by a table's "View" action (Figma
 * 1293:53438). Pages render it *in place of* their heading, search and tabs:
 * "Go Back" pill
 * and the sub-tab's category chip on top, then a white card (name + close,
 * a green details box, "Last Updated") beside the map.
 * Works for Animal Health records (location, hours, contact person),
 * Processing records (address, products, capacity, registration) and
 * Veterinary Professionals (practice, specialisation, licence).
 *
 * Props
 *   record       the row the visitor clicked "View" on
 *   onBack       return to the table (Go Back and the card's close button)
 *   badge        optional status pill, shown beside the record's type
 *   label        optional category chip text (the active sub-tab / tab)
 *   lastUpdated  optional date for the card's footer
 */
export default function FacilityMapView({ record, onBack, badge, label, lastUpdated }) {
  const { origin, locating, error: geoError, requestDirections } = useDirectionsOrigin();

  // The view replaces the page's top section, so bring the visitor up to it
  useEffect(() => {
    scrollToTop();
  }, [record.id]);
  const destination = coordsOf(record);
  const mapProps = origin ? { directions: { origin, destination } } : { place: destination };

  const capacity = record.capacity != null
    ? `${Number(record.capacity).toLocaleString('en-NG')} ${record.unit || ''}`.trim()
    : null;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button
          type="button"
          onClick={onBack}
          className="flex w-fit items-center gap-2 rounded-full border border-[#e5e5e5] bg-white px-4 py-2 text-[12px] font-medium text-black shadow-sm transition-colors hover:text-[#21990f]"
        >
          <FiArrowLeft className="h-4 w-4" />
          Go Back
        </button>
        {label && (
          <span className="rounded-[6px] bg-[#e3f6df] px-3 py-2 text-[12px] font-medium text-[#21990f]">
            {label}
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-[300px_1fr] pub-xl:grid-cols-[380px_1fr]">
        <div className="flex h-fit flex-col gap-3 rounded-[10px] border-[0.55px] border-[#8e8e8e] bg-white p-[10px]">
          <div className="flex items-start justify-between gap-3 px-1 pt-1">
            <h3 className="text-[14px] font-bold text-black">{record.name}</h3>
            <button
              type="button"
              onClick={onBack}
              aria-label="Close map view"
              className="shrink-0 text-[#8e8e8e] transition-colors hover:text-black"
            >
              <FiX className="h-4 w-4" />
            </button>
          </div>

          <div className="flex flex-col gap-2 rounded-[6px] bg-[rgba(40,186,18,0.1)] p-[10px]">
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-[12px] font-medium text-[#555]">{record.type || record.practice}</p>
              {badge}
            </div>
            <p className="text-[12px] text-[#8e8e8e]">
              Location:{' '}
              <span className="underline underline-offset-2">{record.location || record.address || record.state}</span>
            </p>
            <Detail label="Practice / Facility" value={record.type && record.practice} />
            <Detail label="Specialisation" value={record.specialisation} />
            <Detail label="Services Available" value={record.services} />
            <Detail label="Products Processed" value={record.products} />
            <Detail label="Source Species" value={record.species} />
            <Detail label="Capacity" value={capacity} />
            <Detail label="Ownership" value={record.ownership} />
            <Detail label="Registration No." value={record.registrationNumber || record.licence} />
            <Detail label="Certification" value={record.certification} />
            <Detail label="Last Inspected" value={record.lastInspected} />
            {record.operatingDays && <StationHours station={record} />}
            <Detail label="Contact Name" value={record.contactName} />
            <Detail label="Contact No." value={record.contactPhone || record.contact} />
            <Detail label="Email" value={record.email} />

            <button
              type="button"
              onClick={requestDirections}
              disabled={locating}
              className="w-fit text-[12px] font-semibold text-green-700 underline underline-offset-2 hover:text-green-800 disabled:opacity-60"
            >
              {locating ? 'Locating you...' : 'Get Directions'}
            </button>
            {geoError && (
              <p className="rounded-lg border border-amber-200 bg-amber-50 p-2 text-[11px] font-medium leading-snug text-amber-800">
                {geoError}
              </p>
            )}
          </div>

          {lastUpdated && (
            <p className="border-t border-[#e5e5e5] px-1 pt-2 text-[10px] text-[#8e8e8e]">
              Last Updated: {lastUpdated}
            </p>
          )}
        </div>

        <div className="h-[260px] overflow-hidden rounded-[10px] border-[0.55px] border-[#8e8e8e] sm:h-[400px] lg:h-[596px]">
          <GoogleMapEmbed title={record.name} {...mapProps} />
        </div>
      </div>
    </div>
  );
}
